import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

import Alert from "@mui/material/Alert";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import LinearProgress from "@mui/material/LinearProgress";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import { useLocation } from "react-router-dom";
import { gsap } from "gsap";

import { getProductsByBrand, getProductsByCategory } from "../api/apiCalls";
import FeaturedCollectionProduct from "./FeaturedCollectionProduct";
import { createBreadcrumbSchema, usePageSeo } from "../utils/seo";
import { getCategorySeo } from "../utils/categorySeo";

function getCategoryHeading(products) {
  if (!products?.length) {
    return {
      eyebrow: "Buzz Mobile",
      title: "Категории",
      description: "Избрани производи и додатоци за вашиот следен избор.",
    };
  }

  if (products[0].categories[0] === "mgalanterija") {
    return {
      eyebrow: "Мобилна опрема",
      title: "Мобилна галантерија",
      description:
        "Полначи, кабли, слушалки и секојдневни додатоци со внимателно избран асортиман.",
    };
  }

  if (products[0].categories[0] === "kgalanterija") {
    return {
      eyebrow: "Setup essentials",
      title: "Компјутерска галантерија",
      description:
        "Гејминг и desk accessories со чист изглед, удобност и сигурни перформанси.",
    };
  }

  return {
    eyebrow: "Избор по бренд",
    title: `Мобилни телефони - ${products[0].brand}`,
    description:
      "Премиум модели, свеж избор и брз преглед на најдобрите уреди од брендот што го бараш.",
  };
}

function getSortOptions() {
  return [
    "Релевантност",
    "Датум на објава",
    "Цена: Ниска - Висока",
    "Цена: Висока - Ниска",
  ];
}

function getProductSortPrice(product) {
  if ("priceStorage" in product && product.priceStorage?.length) {
    return Number(product.priceStorage[0].price) || 0;
  }

  return Number(product.price) || 0;
}

function Category() {
  const [filter, setFilter] = useState("Релевантност");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortedProducts, setSortedProducts] = useState([]);
  const sectionRef = useRef(null);

  const location = useLocation();
  const categoryTitle = location.pathname.split("/")[1];
  const brand = location.pathname.split("/")[2];
  const categorySeo = getCategorySeo({
    section: categoryTitle,
    category: brand,
  });

  useEffect(() => {
    let isMounted = true;

    setLoading(true);

    const request =
      categoryTitle === "telefoni"
        ? getProductsByBrand(brand)
        : getProductsByCategory(brand);

    request.then(
      (res) => {
        if (!isMounted) {
          return;
        }

        const nextProducts = Array.isArray(res) ? res : [];
        setProducts(nextProducts);
        setSortedProducts(nextProducts);
        setFilter("Релевантност");
        setLoading(false);
      },
      (err) => {
        console.log(err);

        if (!isMounted) {
          return;
        }

        setProducts([]);
        setSortedProducts([]);
        setLoading(false);
      }
    );

    return () => {
      isMounted = false;
    };
  }, [brand, categoryTitle]);

  useEffect(() => {
    if (!products.length) {
      return;
    }

    if (categoryTitle === "telefoni") {
      const title = products[0].categories[0];
      document.title = `${
        title[0].toUpperCase() + title.slice(1)
      } | Buzz Mobile - Премиум мобилара`;
      return;
    }

    document.title = "Галантерија | Buzz Mobile - Премиум мобилара";
  }, [categoryTitle, products]);

  usePageSeo({
    ...categorySeo,
    structuredData: createBreadcrumbSchema([
      { name: "Buzz Mobile", path: "/" },
      { name: categorySeo.categoryName, path: categorySeo.canonicalPath },
    ]),
  });

  useLayoutEffect(() => {
    if (!sectionRef.current || loading) {
      return undefined;
    }

    const ctx = gsap.context(() => {
      gsap.from(".category__hero", {
        y: 36,
        opacity: 0,
        duration: 0.75,
        ease: "power3.out",
      });

      gsap.from(".category__filter-card", {
        y: 28,
        opacity: 0,
        duration: 0.6,
        ease: "power3.out",
        delay: 0.08,
      });

      gsap.from(".category__phones .featured__collection__singlep", {
        y: 28,
        opacity: 0,
        duration: 0.65,
        stagger: 0.06,
        ease: "power3.out",
        delay: 0.14,
      });
    }, sectionRef);

    return () => ctx.revert();
  }, [loading, sortedProducts]);

  const filterProducts = (event) => {
    const value = event.target.value;
    setFilter(value);

    if (value === "Релевантност") {
      setSortedProducts(products);
      return;
    }

    if (value === "Датум на објава") {
      setSortedProducts(
        products
          .slice()
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      );
      return;
    }

    if (value === "Цена: Ниска - Висока") {
      setSortedProducts(
        products
          .slice()
          .sort((a, b) => getProductSortPrice(a) - getProductSortPrice(b))
      );
      return;
    }

    if (value === "Цена: Висока - Ниска") {
      setSortedProducts(
        products
          .slice()
          .sort((a, b) => getProductSortPrice(b) - getProductSortPrice(a))
      );
    }
  };

  const heading = getCategoryHeading(products);

  return (
    <section className="category__container" ref={sectionRef}>
      <div className="category__wrapper container">
        <div className="category__hero">
          <div className="category__heading">
            <h2 className="title__h2">{heading.title}</h2>
          </div>
        </div>

        {loading ? (
          <div className="category__loading">
            <LinearProgress />
          </div>
        ) : (
          <div className="category__content">
            {sortedProducts.length ? (
              <div className="category__filter-card">
                <div>
                  <span className="category__filter-label">Сортирај</span>
                  <p className="category__filter-copy">
                    Прегледај ги моделите според најнови уреди, цена или
                    релевантност.
                  </p>
                </div>

                <FormControl className="category__select">
                  <InputLabel>Сортирај</InputLabel>
                  <Select
                    value={filter}
                    label="Сортирај"
                    onChange={filterProducts}
                  >
                    {getSortOptions().map((option) => (
                      <MenuItem value={option} key={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            ) : null}

            <div className="category__phones">
              {sortedProducts.length ? (
                sortedProducts.map((product) => (
                  <FeaturedCollectionProduct
                    product={product}
                    key={product._id}
                    category={true}
                  />
                ))
              ) : (
                <Alert severity="warning">Категоријата е празна</Alert>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

export default Category;
